// 261023 E3-P3h1 (H4 / K4 / K7) — hàm THUẦN của CỘT BƯỚC ①–⑤: mỗi bước nói «✓ xong · đang chạy · chưa chạy · Chặng n · cần bạn · x/y».
// KHÔNG import antd / react (jest của react-scripts không load được antd v6). `StageNav.tsx` chỉ vẽ.
// Nguồn dữ liệu: CHỈ `GET workbench` + `rawChapters` đã có ở vỏ — không gọi thêm route. Chỗ không suy được (③ số từ vựng, ① chương đã quét từng cái) KHÔNG hiện số, không bịa.
// Luật «✓ chỉ khi xong thật» (V3): x = y VÀ không còn chấm cam/đỏ VÀ không «cần bạn».
import type { AppendRun, WorkbenchEpisode, WorkbenchStage, WorkbenchStageKey, WorkbenchView } from '../../../types/adaptationWorkbench';
import type { RawChapterFinding } from '../../../types/adaptation';
import { isNewStoryRun, isNewStoryView, nsCursor, nsCursorStage } from './newStoryRunView';
import type { Cursor } from './newStoryRunView';
import { needsRecheckOf, pushBlockedOf } from './episodeRowView';
import { episodeChip } from './scriptStageView';
import { STAGE_ORDER } from './workbenchView';
import type { StageMarkView } from './workbenchView';

export type NavStatus = 'done' | 'running' | 'notRun' | 'needYou' | 'idle' | 'locked';
export interface NavXY { x: number; y: number; unit: string }
export interface StageNavRow {
  key: WorkbenchStageKey;
  /** Dấu trong vòng tròn: ✓ khi xong thật, không thì số bước (1–5). */
  glyph: string;
  /** Dòng phụ dưới tên bước: «chưa chạy · Chặng 1» · «đang chạy…» · «2/3 xong» · «xong» · «3 chương» · «» (rỗng). */
  label: string;
  status: NavStatus;
  /** ✓ xong thật (lùi mờ khi không phải bước đang xem). */
  done: boolean;
  xy: NavXY | null;
  /** 0–100 cho thanh tiến độ mảnh; null khi không có x/y. */
  pct: number | null;
  /** Chấm: cam = còn việc không chặn · đỏ = có lỗi/chặn. `dotText` = số việc. */
  dot: 'warn' | 'bad' | null;
  dotText: string;
  needYou: boolean;
  /** Trạng thái này do CHUỖI «Truyện mới» quyết (chưa chạy tới / đang chạy bước này) — chỉ khi đó hộp V12 thay thân bước (K4/K5). Job làm tay đang chạy thì KHÔNG. */
  byChain: boolean;
  /** Nhãn «cần bạn» (rỗng khi không cần). */
  needTag: string;
  tip: string;
}

export interface StageNavOpts {
  /** Id đợt chuỗi đã đóng (DONE) ở trình duyệt này — đã đóng thì dải ẩn và cột quay về nhãn theo dữ liệu. */
  dismissedId: string | null;
  /** Số việc «cần bạn» của Sổ duyệt (`reviewEntryView.needCount`); ⑤ chỉ mang nhãn «cần bạn» khi Sổ không còn mục nào. */
  reviewNeed: number;
  /** `pushStripOf().mode === 'allPushed'` (đã theo luật H11a: mọi tập đã viết đều đã đẩy). */
  allPushed: boolean;
  /** Chương nguồn (để đếm chương có cảnh báo/lỗi chưa chấp nhận). Vắng ⇒ chỉ dùng `stages`. */
  rawChapters?: ReadonlyArray<{ findings?: RawChapterFinding[] }>;
}

export const NEED_TAG = 'cần bạn';
/** Số Chặng của từng bước (①② = Chặng 1; ③④⑤ = Chặng 2). */
export const STEP_CHANG: Record<WorkbenchStageKey, 1 | 2> = { source: 1, skeleton: 1, localize: 2, split: 2, script: 2 };

// ─── Khi nào dùng nhãn chuỗi (K4) ────────────────────────────────────────────

export interface ChainInfo { mode: 'start' | 'run'; cursor: Cursor | null; run: AppendRun | null }

/**
 * Cột dùng nhãn chuỗi («chưa chạy · Chặng n», «đang chạy», «cần bạn») CHỈ khi truyện chạy theo chuỗi «Truyện mới»: đang ở nút «Bắt đầu», hoặc có chuỗi NEW_STORY còn sống
 * (kể cả DONE chưa đóng). Truyện cũ / APPEND / chuỗi đã huỷ / DONE đã đóng ⇒ null (nhãn theo dữ liệu thật, không hứa «máy sẽ tự chạy»).
 */
export const chainOf = (w: WorkbenchView | null | undefined, dismissedId: string | null): ChainInfo | null => {
  if (!w) return null;
  const run = w.appendRun ?? null;
  if (run) {
    if (!isNewStoryRun(run) || run.status === 'CANCELLED') return null;
    if (run.status === 'DONE' && run.id === dismissedId) return null;
    return { mode: 'run', cursor: nsCursor(w), run };
  }
  if (w.newStoryRun) return { mode: 'run', cursor: nsCursor(w), run: null };
  if (w.canStartNewStory?.available) return { mode: 'start', cursor: null, run: null };
  return null;
};

// ─── Dòng theo dữ liệu thật ─────────────────────────────────────────────────

const WAIT_BLOCKERS: ReadonlySet<string> = new Set(['SKELETON_NOT_APPROVED', 'OVERVIEW_MISSING']);   // «chờ duyệt» ≠ lỗi: không tô đỏ

const unacceptedFindings = (raws: StageNavOpts['rawChapters']): { error: number; warn: number } => {
  let error = 0; let warn = 0;
  (raws ?? []).forEach((r) => (r.findings ?? []).forEach((f) => { if (f && !f.accepted) { if (f.severity === 'ERROR') error += 1; else warn += 1; } }));
  return { error, warn };
};

// 3k-c (SA N5): CÙNG luật với dòng tập (`episodeRowView`): lỗi giám khảo · «Cần kiểm lại» · BE khoá đẩy ⇒ không sạch ⇒ không ✓ (dòng nói «Cần xem» thì cột không được ✓).
const cleanEpisode = (e: WorkbenchEpisode): boolean => episodeChip(e).key !== 'review' && !(e.c2Unresolved ?? []).some((u) => u?.target === true) && !needsRecheckOf(e) && !pushBlockedOf(e);

const xyLabel = (xy: NavXY | null): string => (xy ? `${xy.x}/${xy.y} xong` : '');
const pctOf = (xy: NavXY | null): number | null => (xy && xy.y > 0 ? Math.round((100 * xy.x) / xy.y) : null);

const blank = (key: WorkbenchStageKey): StageNavRow => ({ key, glyph: String(STAGE_ORDER.indexOf(key) + 1), label: '', status: 'idle', done: false, xy: null, pct: null, dot: null, dotText: '', needYou: false, byChain: false, needTag: '', tip: '' });

const dataRow = (w: WorkbenchView, key: WorkbenchStageKey, opts: StageNavOpts): StageNavRow => {
  const st: WorkbenchStage | undefined = w.stages?.find((s) => s.key === key);
  const row = blank(key);
  if (!st) return row;
  const f = w.outlineFlags;
  if (st.state === 'locked') return { ...row, status: 'locked', label: 'chưa tới — xong bước trước', tip: 'Cần xong bước trước' };
  const running = st.state === 'running';

  let xy: NavXY | null = null;
  let red = 0; let cam = 0;
  let todoLabel = 'chưa làm';

  if (key === 'source') {
    const total = f?.rawChapters ?? 0;
    if (total > 0 && st.state === 'done') xy = { x: total, y: total, unit: 'chương đã quét' };
    const fi = unacceptedFindings(opts.rawChapters);
    red = st.blockers.length + fi.error;
    cam = st.attention.length + fi.warn;
    todoLabel = 'chưa có chương';
  } else if (key === 'skeleton') {
    if (f?.status) xy = { x: f.status === 'APPROVED' ? 1 : 0, y: 1, unit: 'đã duyệt' };
    red = st.blockers.filter((b) => !WAIT_BLOCKERS.has(b.code)).length;
    cam = st.attention.length;
  } else if (key === 'localize') {
    red = st.blockers.length;
    cam = st.attention.length;
  } else if (key === 'split') {
    if (w.episodePlan?.state) xy = { x: w.episodePlan.state === 'APPROVED' ? 1 : 0, y: 1, unit: 'đã chốt dàn tập' };
    red = st.blockers.length;
    cam = st.attention.length;
  } else {
    const y = f?.episodesListed ?? 0;
    const eps = w.episodes ?? [];
    if (y > 0) xy = { x: eps.filter((e) => e.handoff === 'up_to_date' && cleanEpisode(e)).length, y, unit: 'tập đã đẩy và hết lỗi' };
    red = eps.filter((e) => !cleanEpisode(e)).length;
    cam = red > 0 ? 0 : eps.filter((e) => e.written === 'draft' && e.handoff !== 'up_to_date').length;
  }

  const dot: StageNavRow['dot'] = red > 0 ? 'bad' : cam > 0 ? 'warn' : null;
  const dotText = red > 0 ? String(red) : cam > 0 ? String(cam) : '';
  const complete = !running && (st.state === 'done' || key === 'script') && (xy ? xy.x === xy.y && xy.y > 0 : st.state === 'done') && dot === null;
  const total = f?.rawChapters ?? 0;
  const label = running ? 'đang chạy…'
    : st.state === 'todo' ? (key === 'source' && total > 0 ? `${total} chương` : todoLabel)
      : xy ? xyLabel(xy) : complete ? 'xong' : key === 'source' && total > 0 ? `${total} chương` : '';
  const status: NavStatus = running ? 'running' : complete ? 'done' : 'idle';
  const tip = running ? 'Đang có việc chạy nền ở bước này'
    : complete ? 'Xong'
      : dot === 'bad' ? 'Có lỗi hoặc việc đang chặn'
        : dot === 'warn' ? 'Còn việc dở nhưng không chặn' : st.state === 'todo' ? 'Chưa làm' : '';
  return { ...row, glyph: complete ? '✓' : row.glyph, label, status, done: complete, xy, pct: pctOf(xy), dot, dotText, tip };
};

// ─── Dòng theo chuỗi ────────────────────────────────────────────────────────

const NEED_TIP: Record<string, string> = {
  gate: 'Khung xương đã dựng, chờ bạn duyệt',
  wait: 'Chờ bạn chốt',
  budget: 'Chờ bạn quyết để chạy tiếp',
  review: 'Còn lỗi cổng AI, chờ bạn quyết',
  stopped: 'Đã dừng — bấm “Chạy tiếp” ở dải phía trên',
  error: 'Đã dừng vì lỗi — xem dải phía trên',
};

const notRunRow = (key: WorkbenchStageKey): StageNavRow => ({ ...blank(key), byChain: true, status: 'notRun', label: `chưa chạy · Chặng ${STEP_CHANG[key]}`, tip: `Bước này chưa chạy — máy tự chạy ở Chặng ${STEP_CHANG[key]}` });

export const stageNavRows = (w: WorkbenchView | null | undefined, opts: StageNavOpts): StageNavRow[] => {
  if (!w) return STAGE_ORDER.map(blank);
  const chain = chainOf(w, opts.dismissedId);
  const data = (k: WorkbenchStageKey) => dataRow(w, k, opts);

  if (!chain) return STAGE_ORDER.map(data);
  if (chain.mode === 'start') return STAGE_ORDER.map(notRunRow);

  // Chuỗi DONE (Chặng 2 xong, chờ duyệt đẩy hoặc đã đẩy): mọi bước theo dữ liệu thật; ⑤ mang «cần bạn» khi Sổ không còn mục nào và chưa đẩy hết.
  if (chain.run?.status === 'DONE') {
    return STAGE_ORDER.map((k) => {
      const r = data(k);
      if (k === 'script' && !opts.allPushed && opts.reviewNeed === 0 && !r.done) {
        return { ...r, status: 'needYou', needYou: true, needTag: NEED_TAG, tip: 'Máy viết xong — chờ bạn xem lướt rồi bấm “Duyệt & đẩy”' };
      }
      return r;
    });
  }

  const c = chain.cursor;
  if (!c) return STAGE_ORDER.map(data);
  const cur = nsCursorStage(c);
  const ci = STAGE_ORDER.indexOf(cur);
  return STAGE_ORDER.map((k, i) => {
    if (i < ci) return data(k);
    if (i > ci) return notRunRow(k);
    switch (c.mode) {
      case 'run': return { ...blank(k), byChain: true, status: 'running', label: 'đang chạy…', tip: 'Máy đang chạy bước này' };
      case 'blocked': return { ...blank(k), status: 'idle', label: 'đang chờ việc khác', tip: 'Đang chờ việc khác của truyện xong' };
      default: {
        const d = data(k);
        return { ...d, status: 'needYou', done: false, needYou: true, needTag: NEED_TAG, tip: NEED_TIP[c.mode] ?? 'Chờ bạn' };
      }
    }
  });
};

/** Mọi tập ĐÃ VIẾT đều đã đẩy khớp Chapters (có ít nhất một tập). Cùng luật với «Đã đẩy hết» của dải (H11a) nhưng không cần xem thử. */
export const allWrittenPushed = (episodes: ReadonlyArray<Pick<WorkbenchEpisode, 'written' | 'handoff'>> | null | undefined): boolean => {
  const w = (episodes ?? []).filter((e) => e.written === 'draft');
  return (episodes ?? []).some((e) => e.written === 'draft') && w.every((e) => e.handoff === 'up_to_date');
};

/** Chuỗi «Xử lý phần mới» (APPEND, không phải NEW_STORY) vẫn dùng dấu chip cũ (`appendRunView.stageMarks`): chuyển dấu ấy thành trạng thái hàng. */
export const applyAppendMarks = (rows: StageNavRow[], marks: Partial<Record<WorkbenchStageKey, StageMarkView>>): StageNavRow[] =>
  rows.map((r) => {
    const m = marks[r.key];
    if (!m) return r;
    if (m.tone === 'run') return { ...r, status: 'running', label: 'đang chạy…', done: false, needYou: false, needTag: '', tip: m.tip };
    if (m.tone === 'warn') return { ...r, status: 'needYou', done: false, needYou: true, needTag: m.text || NEED_TAG, tip: m.tip };
    if (m.tone === 'lock') return { ...r, status: 'locked', label: 'chờ bước trước', done: false, tip: m.tip };
    return r;
  });

/**
 * Hàng cột bước cuối cùng: dữ liệu thật + chuỗi «Truyện mới»; CHỈ truyện không thuộc chuỗi «Truyện mới» (kể cả sắp «Bắt đầu» hay đợt đã đóng) mới nhận dấu chip cũ của «Phần mới».
 * (Truyện chưa Bắt đầu mà có `newChapters > 0` KHÔNG được thành «cần bạn · mới» — hàng phải là «chưa chạy · Chặng n».)
 */
export const navRowsFor = (
  w: WorkbenchView | null | undefined, opts: StageNavOpts, appendMarks: Partial<Record<WorkbenchStageKey, StageMarkView>>,
): StageNavRow[] => {
  const base = stageNavRows(w, opts);
  if (!w || chainOf(w, opts.dismissedId) || isNewStoryView(w)) return base;
  return applyAppendMarks(base, appendMarks);
};

/** 3k-c — badge «N gợi ý» (việc KHÔNG chặn của Sổ duyệt) đặt trên mục ⑤ của cột bước. Chỉ ⑤, chỉ khi N ≥ 1. */
export const hintBadgeOf = (key: WorkbenchStageKey, hints: number | null | undefined): { label: string; title: string } | null => {
  if (key !== 'script' || typeof hints !== 'number' || !Number.isFinite(hints) || hints < 1) return null;
  const n = Math.floor(hints);
  return { label: `${n} gợi ý`, title: `Kịch bản — ${n} gợi ý để xem trong Sổ duyệt (không chặn)` };
};
